import { useBodyLock } from '@app/utils/hooks/useBodyLock';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { flattenTree, removeChildrenOf } from '@app/utils/sortable';
import { Sortable } from '@typings';
import React from 'react';

interface Context {
  activeAnnouncement: Nullable<string>;
  activeId: Nullable<Sortable.Id>;
  flattenedItems: Sortable.FlattenedItem[];
  overId: Nullable<Sortable.Id>;
  setActiveAnnouncement: (value: string) => void;
  setActiveId: (value: Nullable<Sortable.Id>) => void;
  setOverId: (value: Nullable<Sortable.Id>) => void;
}

export const useSortableContext = () => {
  const context = React.useContext(SortableContext);

  if (!isDefined(context)) {
    throw new Error('SortableContext can not be used outside the scope of SortableContextProvider');
  }

  return context;
};

export const SortableContext = React.createContext<Context | null>(null);

interface Props {
  items: Sortable.Items;
}

export const SortableContextProvider = ({ children, items }: React.PropsWithChildren<Props>) => {
  const [activeId, setActiveId] = React.useState<Nullable<Sortable.Id>>(null);
  const [overId, setOverId] = React.useState<Nullable<Sortable.Id>>(null);
  const [activeAnnouncement, setActiveAnnouncement] = React.useState<Nullable<string>>(null);

  useBodyLock(isDefined(activeId));

  const flattenedItems = React.useMemo(() => {
    const flattenedTree = flattenTree(items);
    const collapsedItemIds = flattenedTree.filter(item => item.collapsed && !isEmpty(item.children ?? [])).map(item => item.id);

    return removeChildrenOf(flattenedTree, isDefined(activeId) ? [activeId, ...collapsedItemIds] : collapsedItemIds);
  }, [activeId, items]);

  return (
    <SortableContext.Provider
      value={{
        activeAnnouncement,
        activeId,
        flattenedItems,
        overId,
        setActiveAnnouncement,
        setActiveId,
        setOverId,
      }}
    >
      {children}
    </SortableContext.Provider>
  );
};
